import React, { useContext, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import {
  NavItem,
  NavLink,
  Button,
  Card,
  InputGroup,
  InputGroupAddon,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSlidersH, faSearch } from '@fortawesome/free-solid-svg-icons';
import css from 'styled-jsx/css';

import {
  CORE,
  CORE_READ,
  GEOFENCE,
  GEOFENCE_READ,
  MAINTENANCE,
  MAINTENANCE_READ,
} from '../../Constants/RBAC';
import {
  CSV_REPORTS,
  ENGINEANALYSIS,
  FLEET,
  MAINTENANCE as MAINTENANCE_ROUTE,
  MAINTENANCE_REPORT,
  REPORTS,
  WATERTRANSFER,
} from '../../Constants/Routes';
import CurrentOrgContext from '../../Context/CurrentOrgContext';
import ToggleSwitch from '../Map/ToggleSwitch';
import Can from '../../RBAC/Can';

const { className: navLinkClass, styles: navLinkStyles } = css.resolve`
  a {
    color: var(--text-gray);
  }
  a:hover,
  .active {
    color: var(--brand-primary) !important;
  }
`;

const { className: dropdownClassName, styles: dropdownStyles } = css.resolve`
  li {
    color: var(--text-gray) !important;
  }
  li:hover,
  .active {
    color: var(--brand-primary) !important;
  }
`;
const { className: itemClassName, styles: itemStyles } = css.resolve`
  a {
    color: var(--text-gray) !important;
    outline: none !important;
  }
  a:hover,
  .active {
    background: transparent;
    color: var(--brand-primary) !important;
  }
`;
const { className: partsMenuClassName, styles: partsMenuStyles } = css.resolve`
  .dropdown-menu {
    z-index: 9999;
  }
`;

export function TopLevelNav({ path, isActive, children, ...rest }) {
  return (
    <NavItem className="d-flex align-items-center mx-2">
      <NavLink
        active={isActive}
        className={`${navLinkClass} transition-fade`}
        tag={Link}
        to={path}
        {...rest}
      >
        {children}
      </NavLink>
      {navLinkStyles}
    </NavItem>
  );
}

TopLevelNav.propTypes = {
  path: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  isActive: PropTypes.bool,
  children: PropTypes.node,
  replace: PropTypes.bool,
};

TopLevelNav.defaultProps = { path: '' };

export function MaintenanceDropdown({
  partsIsActive,
  scheduleIsActive,
  serviceIsActive,
}) {
  const { features: { parts } = {} } = useContext(CurrentOrgContext) || {};

  return (
    <UncontrolledDropdown
      active={partsIsActive || serviceIsActive || scheduleIsActive}
      className={`${dropdownClassName} d-flex align-items-center mx-2`}
      inNavbar
      nav
    >
      <DropdownToggle
        className="h-100 d-flex align-items-center transition-fade text-reset text-decoration-none py-0 shadow-none"
        color="link"
        nav
      >
        Maintenance
      </DropdownToggle>
      <DropdownMenu
        className={`mt-2 shadow ${partsIsActive ? partsMenuClassName : ''}`}
      >
        <DropdownItem
          active={serviceIsActive}
          className={`${itemClassName} nine-tenth-text`}
          disabled={serviceIsActive}
          tag={Link}
          to={`${MAINTENANCE_ROUTE}?tab=service`}
        >
          Dashboard
        </DropdownItem>
        <DropdownItem
          active={scheduleIsActive}
          className={`${itemClassName} nine-tenth-text`}
          disabled={scheduleIsActive}
          tag={Link}
          to={`${MAINTENANCE_ROUTE}?tab=schedules`}
        >
          Schedules
        </DropdownItem>
        {parts ? (
          <DropdownItem
            active={partsIsActive}
            className={`${itemClassName} nine-tenth-text`}
            disabled={partsIsActive}
            tag={Link}
            to={`${MAINTENANCE_ROUTE}?tab=parts`}
          >
            Parts
          </DropdownItem>
        ) : null}
      </DropdownMenu>
      {dropdownStyles}
      {itemStyles}
      <style jsx global>{`
        .dropdown-menu {
          outline: none;
        }
      `}</style>
    </UncontrolledDropdown>
  );
}

MaintenanceDropdown.propTypes = {
  partsIsActive: PropTypes.bool,
  scheduleIsActive: PropTypes.bool,
  serviceIsActive: PropTypes.bool,
};

export function ReportsDropdown() {
  const {
    _id: orgID,
    features: {
      csvExport,
      maintenance_v2: { enabled: maintenanceReportsEnabled } = {},
    } = {},
  } = useContext(CurrentOrgContext) || {};
  const { pathname } = useLocation();
  const csvIsActive = useMemo(() => pathname.includes(CSV_REPORTS), [pathname]),
    fleetIsActive = useMemo(() => pathname.includes(FLEET), [pathname]),
    maintenanceReportsIsActive = useMemo(
      () => pathname.includes(MAINTENANCE_REPORT),
      [pathname]
    ),
    utilizationIsActive = useMemo(() => pathname.includes(REPORTS), [pathname]);
  return (
    <UncontrolledDropdown
      active={
        csvIsActive ||
        fleetIsActive ||
        maintenanceReportsIsActive ||
        utilizationIsActive
      }
      className={`${dropdownClassName} d-flex align-items-center mx-2`}
      inNavbar
      nav
    >
      <DropdownToggle
        className="h-100 d-flex align-items-center transition-fade text-reset text-decoration-none py-0 shadow-none"
        color="link"
        nav
      >
        Reports
      </DropdownToggle>
      <DropdownMenu className="mt-2 shadow">
        {csvExport?.signals > 0 || csvExport?.onDemand ? (
          <DropdownItem
            active={csvIsActive}
            disabled={csvIsActive}
            className={`${itemClassName} nine-tenth-text`}
            tag={Link}
            to={`${CSV_REPORTS}?tab=${
              csvExport.signals > 0 ? 'scheduled' : 'on-demand'
            }`}
          >
            CSV Export
          </DropdownItem>
        ) : null}
        <DropdownItem
          active={fleetIsActive}
          disabled={fleetIsActive}
          className={`${itemClassName} nine-tenth-text`}
          tag={Link}
          to={FLEET}
        >
          Fleet
        </DropdownItem>
        {maintenanceReportsEnabled ? (
          <Can
            no={() => null}
            orgID={orgID}
            perform={MAINTENANCE_READ}
            resource={MAINTENANCE}
            yes={() => (
              <DropdownItem
                active={maintenanceReportsIsActive}
                disabled={maintenanceReportsIsActive}
                className={`${itemClassName} nine-tenth-text`}
                tag={Link}
                to={MAINTENANCE_REPORT}
              >
                Maintenance
              </DropdownItem>
            )}
          />
        ) : null}
        {/* <DropdownItem
          active={utilizationIsActive}
          disabled={utilizationIsActive}
          className={`${itemClassName} nine-tenth-text`}
          tag={Link}
          to="/reports?tab=inventory"
        >
          Utilization
        </DropdownItem> */}
      </DropdownMenu>
      {dropdownStyles}
      {itemStyles}
      {partsMenuStyles}
      <style jsx global>{`
        .dropdown-menu {
          outline: none;
        }
      `}</style>
    </UncontrolledDropdown>
  );
}

export function AppsDropdown({ engineanalysisIsActive, waterpumpIsActive }) {
  const { _id: currentOrgID, features: { apps = [] } = {} } =
    useContext(CurrentOrgContext) || {};
  return apps.length ? (
    <UncontrolledDropdown
      active={waterpumpIsActive || engineanalysisIsActive}
      className={`${dropdownClassName} d-flex align-items-center mx-2`}
      inNavbar
      nav
    >
      <DropdownToggle
        className="h-100 d-flex align-items-center transition-fade text-reset text-decoration-none py-0 shadow-none"
        color="link"
        nav
      >
        Apps
      </DropdownToggle>
      <DropdownMenu className="mt-2 shadow">
        {apps.includes('engineanalysis') ? (
          <DropdownItem
            active={engineanalysisIsActive}
            disabled={engineanalysisIsActive}
            className={`${itemClassName} nine-tenth-text`}
            tag={Link}
            to={`${ENGINEANALYSIS}?orgID=${currentOrgID}&viewing=orgsList`}
          >
            Cummins Diagnostics
          </DropdownItem>
        ) : null}
        {apps.includes('waterpump') ? (
          <DropdownItem
            active={waterpumpIsActive}
            disabled={waterpumpIsActive}
            className={`${itemClassName} nine-tenth-text`}
            tag={Link}
            to={`${WATERTRANSFER}?orgID=${currentOrgID}`}
          >
            Water Transfer
          </DropdownItem>
        ) : null}
      </DropdownMenu>
      {dropdownStyles}
      {itemStyles}
    </UncontrolledDropdown>
  ) : null;
}

AppsDropdown.propTypes = {
  engineanalysisIsActive: PropTypes.bool,
  waterpumpIsActive: PropTypes.bool,
};

export function MobileFiltersToggler({ buttonStyles, toggleMobileFilters }) {
  return (
    <Button
      className="bg-transparent shadow-none text-decoration-none text-brand-primary position-absolute"
      onClick={toggleMobileFilters}
      color="link"
      type="button"
      style={{ right: '0.5rem', ...buttonStyles }}
    >
      <FontAwesomeIcon icon={faSlidersH} size="lg" />
    </Button>
  );
}

MobileFiltersToggler.propTypes = {
  buttonStyles: PropTypes.object,
  toggleMobileFilters: PropTypes.func,
};
MobileFiltersToggler.defaultProps = { buttonStyles: {} };

export function MobileFiltersCard({ children, handleSearchTerm, open }) {
  const inputRef = useRef(null),
    location = useLocation(),
    onMap = location.pathname.includes('map');

  return (
    <Card
      className={`${open ? 'd-flex' : 'd-none'} mt-1 mx-4`}
      style={{
        borderRadius: '20px',
        padding: '10px',
        zIndex: '1',
      }}
    >
      <NavItem className="list-unstyled">
        <p
          className="font-weight-medium mb-0 text-text-dark"
          style={{ marginLeft: '7px' }}
        >
          Filters
        </p>
        <div className="mb-0">
          <InputGroup>
            <div className="row w-100 ml-0">
              <InputGroupAddon addonType="prepend">
                <button
                  className="bg-transparent p-0 ml-1 btn btn-link text-decoration-none"
                  onClick={inputRef.current && inputRef.current.focus}
                >
                  <FontAwesomeIcon
                    className="text-text-light-gray"
                    icon={faSearch}
                  />
                </button>
              </InputGroupAddon>
              <input
                className="asset-search w-75 ml-2 rounded-0 border-top-0 border-left-0 border-right-0 shadow-none"
                type="text"
                onChange={handleSearchTerm}
                onKeyDown={(e) => {
                  if (e.keyCode !== 13) return;
                  handleSearchTerm(e);
                  inputRef.current.blur();
                }}
                ref={inputRef}
                placeholder="Search Inventory"
                style={{ outline: 'none' }}
              />
            </div>
          </InputGroup>
          {onMap ? children : null}
        </div>
      </NavItem>
      <style jsx>{`
        .asset-search::placeholder {
          color: #d2d2d2;
        }
      `}</style>
    </Card>
  );
}

MobileFiltersCard.propTypes = {
  children: PropTypes.node,
  handleSearchTerm: PropTypes.func,
  open: PropTypes.bool,
};

export function MobileToggles(props) {
  const {
    handleAlarmAssetToggle,
    handleEcosystemAssetToggle,
    handleGeofenceToggle,
    handleOfflineAssetToggle,
    showEcosystem,
    showGeofences,
    showOfflineAssets,
    showOnlyActiveAlarms,
  } = props;
  const { _id: orgID } = useContext(CurrentOrgContext);
  return (
    <>
      <p className="font-weight-light my-2 ml-2">Show</p>
      <div
        className="font-weight-normal text-text-light-gray"
        style={{
          fontSize: '0.9rem',
          marginLeft: '3px',
        }}
      >
        <Can
          no={() => (
            <ToggleSwitch checked={showEcosystem} disabled>
              Suborg Assets
            </ToggleSwitch>
          )}
          orgID={orgID}
          perform={CORE_READ}
          resource={CORE}
          yes={() => (
            <ToggleSwitch
              onChange={handleEcosystemAssetToggle}
              checked={showEcosystem}
            >
              Suborg Assets
            </ToggleSwitch>
          )}
        />
        <ToggleSwitch
          onChange={handleAlarmAssetToggle}
          checked={showOnlyActiveAlarms}
        >
          Active Alarms
        </ToggleSwitch>
        <ToggleSwitch
          onChange={handleOfflineAssetToggle}
          checked={showOfflineAssets}
        >
          Offline Assets
        </ToggleSwitch>
        <Can
          no={() => null}
          orgID={orgID}
          perform={GEOFENCE_READ}
          resource={GEOFENCE}
          yes={() => (
            <ToggleSwitch
              onChange={handleGeofenceToggle}
              checked={showGeofences}
            >
              Geofences
            </ToggleSwitch>
          )}
        />
      </div>
    </>
  );
}

MobileToggles.propTypes = {
  handleAlarmAssetToggle: PropTypes.func,
  handleEcosystemAssetToggle: PropTypes.func,
  handleGeofenceToggle: PropTypes.func,
  handleOfflineAssetToggle: PropTypes.func,
  showEcosystem: PropTypes.bool,
  showGeofences: PropTypes.bool,
  showOfflineAssets: PropTypes.bool,
  showOnlyActiveAlarms: PropTypes.bool,
};
